// src/auth/AccessGate.jsx
// Gates core dashboard content per tenant (Districts.dashboard_gate):
//   'none'         -> pass through
//   'account'      -> requires a signed-in user (free email-code account)
//   'subscription' -> requires an active subscription (admins pass)
// Distinct from SubscribeGate, which gates individual premium features via
// Districts.premium_gate. Reuses SubscribeGate's .sg-* card styles.
import React, { useState } from "react";
import { Lock } from "lucide-react";
import { useAuth } from "./AuthContext";
import { useSubscription } from "./useSubscription";
import "./SubscribeGate.css";

function AccessGate({ gate = "none", featureName = "This dashboard", children }) {
  const { user, loading: authLoading, openLogin } = useAuth();
  const {
    loading: subLoading,
    status,
    isSubscriber,
    startCheckout,
  } = useSubscription();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Same dev bypass as SubscribeGate; dead code in production builds.
  const devBypass =
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    window.localStorage.getItem("bw:devPremium") === "1";

  if ((gate !== "account" && gate !== "subscription") || devBypass) {
    return <>{children}</>;
  }

  if (authLoading || (gate === "subscription" && subLoading)) {
    return (
      <div className="sg-loading" role="status" aria-label="Loading">
        <span className="sg-spinner" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="sg-card">
        <div className="sg-icon">
          <Lock size={22} strokeWidth={2} aria-hidden="true" />
        </div>
        <h3 className="sg-heading">Sign in to view {featureName.toLowerCase()}</h3>
        <p className="sg-copy">
          {gate === "account"
            ? "Free — we'll email you a 6-digit sign-in code. No password needed."
            : "Sign in to start your 3-day free trial."}
        </p>
        <button type="button" className="sg-button" onClick={openLogin}>
          Sign in
        </button>
      </div>
    );
  }

  if (gate === "subscription" && !isSubscriber) {
    const handleCheckout = async () => {
      setBusy(true);
      setError("");
      const { error: checkoutError } = await startCheckout();
      if (checkoutError) {
        setError(checkoutError);
        setBusy(false);
      }
      // On success the browser navigates to Stripe Checkout.
    };

    return (
      <div className="sg-card">
        <div className="sg-icon">
          <Lock size={22} strokeWidth={2} aria-hidden="true" />
        </div>
        <h3 className="sg-heading">Start your free trial</h3>
        <p className="sg-copy">
          3 days free, cancel anytime. Then it converts to a paid monthly
          subscription.
        </p>
        {error && <p className="sg-error">{error}</p>}
        <button
          type="button"
          className="sg-button"
          onClick={handleCheckout}
          disabled={busy}
        >
          {busy
            ? "Redirecting…"
            : status === "canceled"
            ? "Resubscribe"
            : "Start 3-day free trial"}
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

export default AccessGate;
